import { seatedAssemblyPieces } from "./bench-work/assembly";
import { productBlueprintFor } from "./bench-work/blueprint";
import { PALLET_BOARD_LENGTH_IN } from "./bench-work/pallet-geometry";
import { TutorialFacts } from "./GameState";
import { Machine, MachineId } from "./Machine";
import { Board, MaterialInstance } from "./Materials";
import { scavengeLoot } from "./scavenge";

/**
 * The facts the opening turns on, kept apart from tutorial.ts and
 * story.ts because both read them and neither may import the other at
 * runtime (the story reveals the tutorial's steps; the tutorial's steps
 * name the scenes that reveal them). A thought and the box it reveals
 * ask the same question of the shop — "is the pallet home yet?" — so
 * the question is written once, here.
 */

/** The savings goal that ends the opening's selling stretch. */
export const TUTORIAL_MONEY_GOAL = 300;

/**
 * The machines the opening's last goal counts as "a real machine": the
 * saws the savings goal was sized for. A garbage can or a lumber shelf
 * is furniture, not the purchase the story is pointing at.
 */
export const FIRST_MACHINE_IDS: ReadonlyArray<MachineId> = [
  "miterSaw",
  "jobsiteTableSaw",
  "lunchboxPlaner",
  "jointer",
  "bandSaw",
  "sawhorses",
];

// ---------------------------------------------------------------------------
// Reading the shop
// ---------------------------------------------------------------------------

/**
 * Every loose piece the player could be said to have: in hand, on the
 * floor, sitting on a machine's shelf or in its bay, or riding in the bed.
 * Deliberately broad — a step asking "do you have a shelf yet" shouldn't
 * care that it's still lying in the bench's output bay.
 */
export function shopMaterials(
  gameState: TutorialFacts,
): ReadonlyArray<MaterialInstance> {
  return [
    ...gameState.player.inventory,
    ...gameState.materialPiles.map((pile) => pile.material),
    ...gameState.machines.flatMap((machine) => [
      ...(machine.storedMaterials ?? []),
      ...(machine.inputMaterials ?? []),
      ...(machine.outputMaterials ?? []),
    ]),
    ...gameState.truck.bed,
    ...gameState.stand,
  ];
}

export const isPallet = (material: MaterialInstance) =>
  material.type === "pallet";

export const isRusticShelf = (material: MaterialInstance) =>
  material.type === "rusticShelf";

/** A board off a pallet — the one piece of stock a pallet is made of. */
export const isPalletBoard = (material: MaterialInstance): boolean =>
  material.type === "board" && (material as Board).species === "pallet";

/** A pallet board still at its pried length, uncut. */
export const isFullPalletBoard = (material: MaterialInstance): boolean =>
  isPalletBoard(material) &&
  (material as Board).length >= PALLET_BOARD_LENGTH_IN;

export const countOf = (
  gameState: TutorialFacts,
  match: (material: MaterialInstance) => boolean,
): number => shopMaterials(gameState).filter(match).length;

/** The parts a rustic shelf is built from: six whole pallet boards, one
 * per slot of the rusticShelf plan. Stated here rather than read off
 * the blueprint so the count is legible where the steps are written;
 * the sequence test walks the real build and would catch the two
 * drifting apart. */
export const SHELF_BOARD_COUNT = 6;

export const hasShelfParts = (gameState: TutorialFacts) =>
  countOf(gameState, isPalletBoard) >= SHELF_BOARD_COUNT;

export const hasShelf = (gameState: TutorialFacts) =>
  countOf(gameState, isRusticShelf) > 0;

export const soldFirstPiece = (gameState: TutorialFacts) =>
  gameState.progression.salesCompleted > 0;

/**
 * A pallet turned up on the scavenging trip underway — found at a stop
 * and riding along in the truck, not yet in the bed (it lands there
 * when the trip ends). The trip's loot is the one place a pallet can be
 * that `shopMaterials` doesn't read.
 */
export const palletFoundOnTrip = (gameState: TutorialFacts): boolean => {
  const away = gameState.player.away;
  return away?.kind === "scavenging" && scavengeLoot(away).length > 0;
};

/**
 * Whether the shop has yet to see its first pallet in any form — none
 * found on the trip, none home, none of its boards, no shelf, no sale.
 * The scavenge step reads this, and so does startScavengingAction: while
 * it holds, a scavenging trip plants its find at the first stop, so the
 * trip the card points at can't come home empty.
 */
export const needsFirstPallet = (gameState: TutorialFacts): boolean =>
  !palletFoundOnTrip(gameState) &&
  countOf(gameState, isPallet) === 0 &&
  !hasShelfParts(gameState) &&
  !hasShelf(gameState) &&
  !soldFirstPiece(gameState);

/** The pallet is back at the shop — in the bed, in the arms, on the
 * floor, on a bench — with the trip over. */
export const palletHome = (gameState: TutorialFacts): boolean =>
  gameState.player.away === null && countOf(gameState, isPallet) > 0;

/** A pallet staged on a station's top, where prying happens. */
export const palletOnBench = (gameState: TutorialFacts): boolean =>
  gameState.machines.some((machine) => machine.inputMaterials.some(isPallet));

/**
 * Every slot of the rustic shelf's plan has a board lying on it at some
 * bench with the plan pulled — the bench view's own seating derivation
 * (assembly.ts) read off the persistent layout, so a refresh mid-layout
 * finds the same answer.
 */
export const shelfLaidOut = (gameState: TutorialFacts): boolean =>
  gameState.machines.some((state) => {
    if (state.selectedOperationId !== "buildRusticShelf") return false;
    const blueprint = productBlueprintFor("rusticShelf");
    if (!blueprint) return false;
    const seated = seatedAssemblyPieces(new Machine(state), blueprint);
    return seated.size === blueprint.slots.length;
  });
